import type {
  StoreWebsiteEditorState,
  StoreWebsiteEditorStateV3,
  TemplatePublicStatus,
  WebsitePublishAction,
} from '../../types/website-templates';
import { websiteMediaUrl } from './public-dto';
import { WEBSITE_TEMPLATE_ASSET_REF_PREFIX } from './resolve';

/**
 * C7 storefront entry (A23). A merchant who already has a storefront draft
 * opens that storefront. Only a merchant with nothing to open sees the
 * template library first.
 */
export type StorefrontEntryDecision =
  | {
      readonly screen: 'active-storefront';
      readonly reason: 'template' | 'converted-legacy';
    }
  | {
      readonly screen: 'template-library';
      readonly reason: 'no-template' | 'unmappable-legacy';
    };

export function resolveStorefrontEntry(state: StoreWebsiteEditorState): StorefrontEntryDecision {
  if (state.draft !== null && state.manifest !== null) {
    return {
      screen: 'active-storefront',
      reason: state.template?.kind === 'legacy-frozen' ? 'converted-legacy' : 'template',
    };
  }
  return {
    screen: 'template-library',
    reason: state.conversion === 'unmappable' ? 'unmappable-legacy' : 'no-template',
  };
}

/**
 * Epic #228 P3 entry: the same decisions as `resolveStorefrontEntry`, read
 * from the v3 state. A store with a template to open goes to that template's
 * edit route; the library keeps the same two reasons.
 */
export type StorefrontEntryDecisionV3 =
  | {
      readonly screen: 'edit';
      readonly templateId: string;
      readonly reason: 'template' | 'converted-legacy';
    }
  | {
      readonly screen: 'template-library';
      readonly reason: 'no-template' | 'unmappable-legacy';
    };

export function resolveStorefrontEntryV3(
  state: StoreWebsiteEditorStateV3,
): StorefrontEntryDecisionV3 {
  if (state.head !== null && state.templateId !== null && state.manifest !== null) {
    return {
      screen: 'edit',
      templateId: state.templateId,
      reason: state.template?.kind === 'legacy-frozen' ? 'converted-legacy' : 'template',
    };
  }
  return {
    screen: 'template-library',
    reason: state.conversion === 'unmappable' ? 'unmappable-legacy' : 'no-template',
  };
}

/**
 * Epic #228 P3: `live` exactly when the live revision belongs to this
 * template. Null when the template has no saved draft.
 */
export function deriveTemplatePublicStatus(
  state: StoreWebsiteEditorStateV3,
): TemplatePublicStatus | null {
  if (state.head === null || state.templateId === null) return null;
  return state.live !== null && state.live.templateId === state.templateId ? 'live' : 'not-live';
}

/**
 * Epic #228 P4 (PD12): what the publish CTA reads from the save queue. A
 * structural subset of `DraftSaveSnapshot`, so the toolbar can pass the
 * snapshot as is and a test can pass a literal.
 */
export interface WebsitePublishSaveState {
  readonly status: 'idle' | 'dirty' | 'saving' | 'saved' | 'failed' | 'conflict' | 'paused';
  readonly pendingSlotIds: readonly string[];
  readonly inFlight: boolean;
  readonly hasFailedPayload: boolean;
  readonly rejected: Readonly<Record<string, unknown>>;
}

const PUBLISH_BLOCKING_SAVE_STATUSES: ReadonlySet<WebsitePublishSaveState['status']> = new Set([
  'saving',
  'failed',
  'conflict',
  'paused',
]);

/**
 * Epic #228 P3 toolbar action. `hasUnpublishedChanges` is the server's value
 * (content hashes are compared in SQL only). Epic #228 P4 adds the save
 * queue: without `save` the result is the P3 one.
 *
 * - not live: Publish (disabled with no draft to publish);
 * - live with changes, or with queue work not yet on the server: Publish
 *   changes (never the stale "All changes published");
 * - live without changes: All changes published, disabled.
 * - Disabled while the queue is `saving`, `failed`, `conflict` or `paused`
 *   (design §5.2), and while a server-only rejection is kept (`rejected`):
 *   the publish flow flushes a `dirty` queue itself.
 */
export function derivePublishAction(
  state: StoreWebsiteEditorStateV3,
  save?: WebsitePublishSaveState,
): WebsitePublishAction {
  const hasWork =
    save !== undefined &&
    (save.pendingSlotIds.length > 0 || save.inFlight || save.hasFailedPayload);
  const blocked =
    save !== undefined &&
    (PUBLISH_BLOCKING_SAVE_STATUSES.has(save.status) || Object.keys(save.rejected).length > 0);
  if (deriveTemplatePublicStatus(state) !== 'live') {
    return { label: 'publish', disabled: state.head === null || blocked };
  }
  return state.hasUnpublishedChanges || hasWork
    ? { label: 'publishChanges', disabled: blocked }
    : { label: 'allPublished', disabled: true };
}

export type StorefrontTemplateStatus = 'active' | 'not-published';

/**
 * The status pill of the draft's template. `active`: customers see this
 * template now. A frozen legacy design is active while the store still
 * serves its legacy page, because that page IS the frozen design.
 */
export function deriveStorefrontTemplateStatus(
  state: StoreWebsiteEditorState,
): StorefrontTemplateStatus | null {
  const template = state.template;
  if (template === null) return null;
  if (state.live !== null && state.liveTemplateId === template.templateId) return 'active';
  if (state.mode === 'legacy' && template.kind === 'legacy-frozen') return 'active';
  return 'not-published';
}

/** Template asset key → the URL of the platform image. Null for no key or a key outside the grammar. */
export function websiteTemplateThumbnailSrc(
  assetKey: string | null,
  supabaseUrl: string,
): string | null {
  if (assetKey === null) return null;
  return websiteMediaUrl(`${WEBSITE_TEMPLATE_ASSET_REF_PREFIX}${assetKey}`, supabaseUrl);
}
